import { useCardSheetGestures } from '@mobile/screens/card-details/use-card-sheet-gestures'
import { rh } from '@shared-ui/theme/scale'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Animated,
  Easing,
  type GestureResponderEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  PanResponder,
  type PanResponderGestureState,
  Platform,
} from 'react-native'

// Единый прогресс связывает положение панели с балансом, обложкой и реквизитами.
export function useCardSheet(bodyHeight: number, initialHistoryTop?: number, onCollapse?: () => void | Promise<void>) {
  const progress = useRef(new Animated.Value(0)).current
  const currentRef = useRef(0)
  const dragStartRef = useRef(0)
  const dragBaseDyRef = useRef(0)
  const historyScrollRef = useRef(0)
  const [isDetails, setIsDetails] = useState(false)
  const historyTop =
    bodyHeight > 0
      ? Math.min(initialHistoryTop ?? rh(275), Math.max(rh(120), bodyHeight - rh(180)))
      : (initialHistoryTop ?? rh(275))
  const detailsTop = Math.max(historyTop + rh(1), bodyHeight - rh(84))
  const distance = detailsTop - historyTop
  const expandedProgress = -historyTop / distance

  useEffect(() => {
    const listener = progress.addListener(({ value }) => {
      currentRef.current = value
    })
    return () => {
      progress.removeListener(listener)
      progress.stopAnimation()
    }
  }, [progress])

  const snapTo = useCallback(
    (target: number) => {
      if (target === 1) setIsDetails(true)
      const collapse = target !== 1 ? onCollapse?.() : undefined
      Animated.timing(progress, {
        toValue: target,
        duration: 320,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start(({ finished }) => {
        if (finished && target !== 1) {
          void Promise.resolve(collapse).then(() => setIsDetails(false))
        }
      })
    },
    [progress, onCollapse],
  )

  const showDetails = useCallback((isShow: boolean) => snapTo(isShow ? 1 : 0), [snapTo])

  const finishDrag = useCallback(
    (velocity = 0) => {
      const current = currentRef.current
      let projected = current + (velocity * 200) / distance

      // Инерция не перескакивает центр: пройти его можно только самим жестом.
      if (dragStartRef.current < 0 && current <= 0) projected = Math.min(0, projected)

      if (dragStartRef.current > 0 && current >= 0) projected = Math.max(0, projected)

      const points = [expandedProgress, 0, 1]
      const target = points.reduce((nearest, point) =>
        Math.abs(point - projected) < Math.abs(nearest - projected) ? point : nearest,
      )
      snapTo(target)
    },
    [distance, expandedProgress, snapTo],
  )

  useCardSheetGestures({ progress, currentRef, dragStartRef, distance, expandedProgress, finishDrag })

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        // Скролл списка отдаёт жест панели только у верхней границы.
        onMoveShouldSetPanResponderCapture: (_, gesture) =>
          Math.abs(gesture.dy) > 8 &&
          Math.abs(gesture.dy) > Math.abs(gesture.dx) &&
          (isDetails || (historyScrollRef.current <= 0 && (gesture.dy > 0 || currentRef.current > expandedProgress))),
        onMoveShouldSetPanResponder: (_, gesture) =>
          Math.abs(gesture.dy) > 8 &&
          Math.abs(gesture.dy) > Math.abs(gesture.dx) &&
          (isDetails || (historyScrollRef.current <= 0 && (gesture.dy > 0 || currentRef.current > expandedProgress))),
        onPanResponderGrant: (_, gesture) => {
          progress.stopAnimation()
          dragStartRef.current = currentRef.current
          // Жест мог начаться со скролла списка: панель двигается только от точки перехвата.
          dragBaseDyRef.current = gesture.dy
        },
        onPanResponderMove: (_, gesture) => {
          progress.setValue(
            Math.max(
              expandedProgress,
              Math.min(1, dragStartRef.current + (gesture.dy - dragBaseDyRef.current) / distance),
            ),
          )
        },
        onPanResponderRelease: (_, gesture) => {
          if (Math.abs(gesture.dy) < 8) {
            showDetails(false)
          } else {
            finishDrag(gesture.vy)
          }
        },
        onPanResponderTerminate: () => finishDrag(),
        onPanResponderTerminationRequest: () => false,
      }),
    [distance, expandedProgress, isDetails, progress, showDetails, finishDrag],
  )

  return {
    isDetails,
    progress: progress.interpolate({ inputRange: [0, 1], outputRange: [0, 1], extrapolate: 'clamp' }),
    showDetails,
    gestures: {
      panHandlers: Platform.OS === 'web' ? {} : panResponder.panHandlers,
      // На заголовке забираем касание до Pressable и браузерного скролла.
      headerPanHandlers:
        Platform.OS === 'web'
          ? {}
          : {
              ...panResponder.panHandlers,
              onStartShouldSetResponderCapture: () => true,
              onMoveShouldSetPanResponderCapture: (_: GestureResponderEvent, gesture: PanResponderGestureState) =>
                Math.abs(gesture.dy) > 8 && Math.abs(gesture.dy) > Math.abs(gesture.dx),
              onMoveShouldSetPanResponder: (_: GestureResponderEvent, gesture: PanResponderGestureState) =>
                Math.abs(gesture.dy) > 8 && Math.abs(gesture.dy) > Math.abs(gesture.dx),
            },
      onHistoryScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        historyScrollRef.current = event.nativeEvent.contentOffset.y
      },
      // Оттяжка списка вниз после прокрутки к началу сворачивает панель, как жест за заголовок.
      onHistoryScrollEndDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if (event.nativeEvent.contentOffset.y > -rh(40)) return

        dragStartRef.current = currentRef.current
        snapTo(currentRef.current < 0 ? 0 : 1)
      },
    },
    panelTop: progress.interpolate({
      inputRange: [expandedProgress, 0, 1],
      outputRange: [0, historyTop, detailsTop],
      extrapolate: 'clamp',
    }),
    historyOpacity: progress.interpolate({ inputRange: [0, 0.7, 1], outputRange: [1, 0, 0], extrapolate: 'clamp' }),
    detailsOpacity: progress.interpolate({ inputRange: [0, 0.3, 1], outputRange: [0, 0, 1], extrapolate: 'clamp' }),
  }
}
